import { InputSavedViewDataset } from "@biggeo/bg-server-lib/studio";
import {
    DatabaseType,
    DatasetConfiguration,
    DatasetStyles,
    FilterData,
    FilterItem,
    FilterObject,
    FilterType,
    LogicOperator,
    SavedViewDataset,
    SavedViewDatasetExtended,
    ShapeStyle,
    WhereOperator,
} from "@biggeo/bg-server-lib/studio";
import { GridColDef } from "@biggeo/bg-ui";
import {
    WithPartialValues,
    areValuesEmpty,
    toNonReadonlyArray,
} from "@biggeo/bg-utils";
import * as A from "fp-ts/lib/Array";
import { pipe } from "fp-ts/lib/function";
import compact from "lodash/compact";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import omit from "lodash/omit";
import orderBy from "lodash/orderBy";
import some from "lodash/some";
import { v4 as uuid } from "uuid";
import { match } from "ts-pattern";
import Zod from "zod";

import { ColorSwatchOption } from "../../../common/components/ColorSwatchSelector";
import { heatmapSwatchOptions } from "../../../common/components/HeatMapColorMapper";
import { levelSetPercentiles } from "../../../constants/map.constants";
import {
    LogicOperator as LogicOperatorProto,
    DatabaseType as ProtoDatabaseType,
    FilterType as ProtoFilterType,
    WhereOperator as ProtoWhereOperator,
} from "../../../velocity/aurora/filter";
import { DbColumnType, SchemaRow } from "../../../velocity/aurora/map";
import { MapAction } from "../../mapbox/context";
import {
    getDefaultDatasetConfigurationOptions,
    getDefaultDatasetStyles,
    getMultiFilters,
} from "../../mapbox/context/utils/general-utils";
import {
    MapContextDataset,
    MapContextFilter,
    SocketConnectionMessages,
} from "../../mapbox/context/types";
import { DEFAULT_SHAPE_COLOR } from "../../mapbox/hooks/style-hooks";
import { setDatasetVisibility } from "../../mapbox/utils/data-layers-utils";
import { handleFilterLayers } from "../../mapbox/utils/filtered-data-layers-utils";
import { getMultiFiltersType } from "../../mapbox/context/utils/map-utils";

// ███████╗███╗   ██╗██╗   ██╗███╗   ███╗
// ██╔════╝████╗  ██║██║   ██║████╗ ████║
// █████╗  ██╔██╗ ██║██║   ██║██╔████╔██║
// ██╔══╝  ██║╚██╗██║██║   ██║██║╚██╔╝██║
// ███████╗██║ ╚████║╚██████╔╝██║ ╚═╝ ██║
// ╚══════╝╚═╝  ╚═══╝ ╚═════╝ ╚═╝     ╚═╝

export enum MapFilterCriteriaSection {
    details = "details",
    filterCriteria = "filterCriteria",
    styles = "styles",
}

//  ██████╗ ██████╗ ███╗   ██╗███████╗████████╗ █████╗ ███╗   ██╗████████╗
// ██╔════╝██╔═══██╗████╗  ██║██╔════╝╚══██╔══╝██╔══██╗████╗  ██║╚══██╔══╝
// ██║     ██║   ██║██╔██╗ ██║███████╗   ██║   ███████║██╔██╗ ██║   ██║
// ██║     ██║   ██║██║╚██╗██║╚════██║   ██║   ██╔══██║██║╚██╗██║   ██║
// ╚██████╗╚██████╔╝██║ ╚████║███████║   ██║   ██║  ██║██║ ╚████║   ██║
//  ╚═════╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝   ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═══╝   ╚═╝

export const SOCKET_CONNECTION_MESSAGES: {
    [K in SocketConnectionMessages]: string;
} = {
    connected: "BigGeo Connected",
    disconnectedRetry: "BigGeo Disconnected. Retrying ...",
    disconnected: "BigGeo Disconnected",
};

export const FILTERED_DATA_TABLE_LIMIT = 20;
export const VIEW_DATASET_TABLE_LIMIT = 20;

export const DEFAULT_LOGIC_OPERATOR = LogicOperator.and;

export const DEFAULT_SHAPE_COLOR_OBJECT = {
    color: "#2F5AF0",
    opacity: 0,
};

export const mapFilterCriteriaOperators: MapFilterCriteriaOperators = {
    [FilterType.BooleanType]: [
        WhereOperator.equals,
        WhereOperator.isEmpty,
        WhereOperator.isNotEmpty,
    ],
    [FilterType.StringType]: [
        WhereOperator.equals,
        WhereOperator.contains,
        WhereOperator.startsWith,
        WhereOperator.endsWith,
        WhereOperator.isEmpty,
        WhereOperator.isNotEmpty,
    ],
    [FilterType.NumberType]: [
        WhereOperator.eq,
        WhereOperator.neq,
        WhereOperator.gt,
        WhereOperator.gte,
        WhereOperator.lt,
        WhereOperator.lte,
        WhereOperator.isEmpty,
        WhereOperator.isNotEmpty,
    ],
    [FilterType.DateType]: [
        WhereOperator.eq,
        WhereOperator.neq,
        WhereOperator.gt,
        WhereOperator.gte,
        WhereOperator.lt,
        WhereOperator.lte,
        WhereOperator.isEmpty,
        WhereOperator.isNotEmpty,
    ],
};

// ████████╗██╗   ██╗██████╗ ███████╗
// ╚══██╔══╝╚██╗ ██╔╝██╔══██╗██╔════╝
//    ██║    ╚████╔╝ ██████╔╝█████╗
//    ██║     ╚██╔╝  ██╔═══╝ ██╔══╝
//    ██║      ██║   ██║     ███████╗
//    ╚═╝      ╚═╝   ╚═╝     ╚══════╝

export type MapFilterCriteriaForm = {
    details: MapFilterCriteriaDetails;
    filterCriteria: MapFilterCriteriaDataset[];
    styles: Partial<MapFilterCriteriaStyle>;
};

export type MapFilterCriteriaDetails = {
    readonly name: string;
    readonly description?: string;
};

export type MapFilterCriteriaDataset = {
    readonly mapTemplateDatasetId: string;
    readonly dataSourceId: string;
    readonly label: string;
    readonly collection: string;
    readonly filters: Partial<FilterItem>[];
    readonly logicOperator?: LogicOperator;
    readonly configuration?: DatasetConfiguration;
};

export type MapFilterCriteriaStyle = Omit<DatasetStyles, "dataAggregation"> & {
    dataAggregation: MapFilterCriteriaStyleDataAggregation;
};

export type MapFilterCriteriaStyleDataAggregation = Partial<{
    heatmap: ColorSwatchOption;
    cluster: ColorSwatchOption; // TODO
}>;

export type MapFilterCriteriaOperators = {
    [T in FilterType]: Array<WhereOperator>;
};

// ███████╗ ██████╗██╗  ██╗███████╗███╗   ███╗ █████╗
// ██╔════╝██╔════╝██║  ██║██╔════╝████╗ ████║██╔══██╗
// ███████╗██║     ███████║█████╗  ██╔████╔██║███████║
// ╚════██║██║     ██╔══██║██╔══╝  ██║╚██╔╝██║██╔══██║
// ███████║╚██████╗██║  ██║███████╗██║ ╚═╝ ██║██║  ██║
// ╚══════╝ ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝     ╚═╝╚═╝  ╚═╝

export const mapFilterCriteriaSchema = Zod.object({
    details: Zod.object({
        name: Zod.string(),
        description: Zod.string().optional(),
    }),
    filterCriteria: Zod.array(
        Zod.object({
            mapTemplateDatasetId: Zod.string(),
            dataSourceId: Zod.string(),
            label: Zod.string(),
            collection: Zod.string(),
            logicOperator: Zod.nativeEnum(LogicOperator).optional(),
            filters: Zod.array(
                Zod.object({
                    column: Zod.string(),
                    type: Zod.nativeEnum(FilterType),
                    operator: Zod.nativeEnum(WhereOperator),
                    value: Zod.string(),
                })
            ),
        })
    ).nonempty(),
    styles: Zod.object({
        shape: Zod.nativeEnum(ShapeStyle).optional(),
        fill: Zod.object({
            color: Zod.string().optional(),
            opacity: Zod.number().optional(),
        }).optional(),
        stroke: Zod.object({
            color: Zod.string().optional(),
            opacity: Zod.number().optional(),
        }).optional(),
        customMarker: Zod.string().optional(),
        dataAggregation: Zod.object({
            heatmap: Zod.object({
                id: Zod.union([Zod.number(), Zod.string()]),
                swatch: Zod.array(
                    Zod.object({
                        range: Zod.number(),
                        color: Zod.string(),
                    })
                ),
            }).optional(),
            cluster: Zod.object({
                id: Zod.union([Zod.number(), Zod.string()]),
                swatch: Zod.array(
                    Zod.object({
                        range: Zod.number(),
                        color: Zod.string(),
                    })
                ),
            }).optional(),
        }).optional(),
    }).optional(),
});

// ██╗  ██╗███████╗██╗     ██████╗ ███████╗██████╗
// ██║  ██║██╔════╝██║     ██╔══██╗██╔════╝██╔══██╗
// ███████║█████╗  ██║     ██████╔╝█████╗  ██████╔╝
// ██╔══██║██╔══╝  ██║     ██╔═══╝ ██╔══╝  ██╔══██╗
// ██║  ██║███████╗███████╗██║     ███████╗██║  ██║
// ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝     ╚══════╝╚═╝  ╚═╝

export const mapFilterCriteriaSign = ({
    operator,
    verbose = false,
}: { operator: WhereOperator; verbose?: boolean }): string =>
    match(operator)
        .with(WhereOperator.eq, () => (verbose ? "Equals" : "="))
        .with(WhereOperator.neq, () => (verbose ? "Not Equals" : "!="))
        .with(WhereOperator.gt, () => (verbose ? "Greater Than" : ">"))
        .with(WhereOperator.gte, () =>
            verbose ? "Greater Than Or Equals" : ">="
        )
        .with(WhereOperator.lt, () => (verbose ? "Less Than" : "<"))
        .with(WhereOperator.lte, () => (verbose ? "Less Than Or Equals" : "<="))
        .with(WhereOperator.isEmpty, () => "Is Empty")
        .with(WhereOperator.isNotEmpty, () => "Is Not Empty")
        .with(WhereOperator.startsWith, () => "Starts With")
        .with(WhereOperator.endsWith, () => "Ends With")
        .with(WhereOperator.equals, () => "Equals")
        .otherwise(() => "Contains");

export const getFilterCriteriaOperators = (type: FilterType): WhereOperator[] =>
    mapFilterCriteriaOperators[type];

export const mapColumnType = (type: DbColumnType): FilterType =>
    match(type)
        .with(DbColumnType.BOOLEAN, () => FilterType.BooleanType)
        .with(DbColumnType.DATE, () => FilterType.DateType)
        .with(DbColumnType.NUMBER, () => FilterType.NumberType)
        .otherwise(() => FilterType.StringType);

export const mapFilterData = (type: FilterType, value?: string): FilterData => {
    return {
        ...(isEqual(type, FilterType.BooleanType) && {
            booleanData: value ? value === "true" : null,
        }),
        ...(isEqual(type, FilterType.DateType) && {
            dateData: value ? new Date(value) : null,
        }),
        ...(isEqual(type, FilterType.NumberType) && {
            numberData: value ? Number.parseInt(value) : null,
        }),
        ...(isEqual(type, FilterType.StringType) && {
            stringData: value ? value : null,
        }),
    };
};

export const mapFilterValue = (
    type: FilterType,
    data: FilterData
): string | undefined => {
    return match(type)
        .with(FilterType.BooleanType, () =>
            data.booleanData ? data.booleanData.toString() : undefined
        )
        .with(FilterType.DateType, () =>
            data.dateData ? data.dateData.toString() : undefined
        )
        .with(FilterType.NumberType, () =>
            data.numberData ? data.numberData.toString() : undefined
        )
        .with(FilterType.StringType, () =>
            data.stringData ? data.stringData.toString() : undefined
        )
        .exhaustive();
};

export const getFilterObjectFromFilter = (
    filterCriteria: MapFilterCriteriaDataset
): FilterObject => {
    const filters = pipe(
        filterCriteria.filters,
        compact,
        A.map((item) => {
            return {
                column: item.column,
                operator: item.operator,
                type: item.type,
                data: item.data,
            };
        }),
        A.map((item) =>
            areValuesEmpty(item) ? undefined : (item as FilterItem)
        ),
        compact
    );

    return {
        collection: filterCriteria.collection,
        databaseId: filterCriteria.dataSourceId,
        databaseType: DatabaseType.point,
        logicOperator: filterCriteria.logicOperator ?? DEFAULT_LOGIC_OPERATOR,
        options:
            filterCriteria.configuration?.options ||
            getDefaultDatasetConfigurationOptions(),
        filters,
    };
};

export const mapFilterCriteriaInitialValues = (
    filter?: MapContextFilter
): WithPartialValues<MapFilterCriteriaForm> => {
    // Defaults to primary
    const color = DEFAULT_SHAPE_COLOR;
    const heatmap = heatmapSwatchOptions[1];

    return {
        details: {
            name: filter ? filter.details.name : undefined,
            description: filter ? filter.details.description : undefined,
        },
        filterCriteria: filter?.filterCriteria || [],
        styles: {
            shape: filter ? filter.styles.shape : ShapeStyle.oval,
            fill: filter
                ? filter.styles.fill
                : color
                  ? {
                        color,
                        opacity: 0.9,
                    }
                  : undefined,
            stroke: filter
                ? filter.styles.stroke
                : color
                  ? {
                        color,
                        opacity: 0.9,
                    }
                  : undefined,
            customMarker: filter ? filter.styles.customMarker : undefined,
            dataAggregation: {
                heatmap: filter?.styles.dataAggregation?.heatmap ?? heatmap,
                cluster: filter
                    ? filter.styles.dataAggregation?.cluster
                    : undefined,
            },
        },
    };
};

export const getTableColumns = (
    columns: SchemaRow[]
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
): GridColDef<any>[] => {
    return columns.reduce((acc, c) => {
        if (acc.some((col) => col.field === c.columnName)) return acc;
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        const v: GridColDef<any> = {
            type: match(c.dataType)
                .with(DbColumnType.STRING, () => "string")
                .with(DbColumnType.BOOLEAN, () => "boolean")
                .with(DbColumnType.NUMBER, () => "number")
                .with(DbColumnType.DATE, () => "date")
                .otherwise(() => "string"),
            field: c.columnName,
            headerName: c.columnName,
            filterable: c.columnName !== "ID",
            valueGetter: (params) => {
                if (c.dataType === DbColumnType.DATE) {
                    return new Date(params.value);
                }

                if (
                    c.dataType === DbColumnType.GEOSPATIAL ||
                    c.dataType === DbColumnType.STRUCTURED
                ) {
                    return JSON.stringify(params.value);
                }
                return params.value;
            },
            sortable: false,
            headerAlign: "left",
            align: "left",
            minWidth:
                c.dataType === DbColumnType.GEOSPATIAL
                    ? 300
                    : match(c.columnName.length * 13)
                          .when(
                              (v) => v > 200,
                              () => 200
                          )
                          .when(
                              (v) => v < 50,
                              () => 50
                          )
                          .otherwise((v) => v),
        };
        return acc.concat(v);
        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    }, [] as GridColDef<any>[]);
};

export const updateDatasetsVisibility = (input: {
    visibility: "visible" | "none";
    map: React.MutableRefObject<mapboxgl.Map | null>;
    isMapLoaded: boolean;
    datasets: MapContextDataset[];
}) => {
    const { datasets, map, isMapLoaded, visibility } = input;

    pipe(
        datasets,
        A.map((dataset) => {
            if (map.current) {
                setDatasetVisibility({
                    map: map.current,
                    prefix: dataset.dataSource.id,
                    levelSets: [],
                    visibility,
                    isMapLoaded,
                    styles: dataset.mapTemplateDataset?.styles || undefined,
                });
            }
        })
    );
};

export const handleFilteredDatasets = (input: {
    dataset: MapContextDataset;
    filters: MapContextFilter[];
    map: React.MutableRefObject<mapboxgl.Map | null>;
    isMapLoaded: boolean;
    dispatch?: React.Dispatch<MapAction>;
    openModal: ({
        onClickClearFilters,
        onClickKeepFilters,
        filters,
    }: {
        onClickClearFilters: () => void;
        onClickKeepFilters: () => void;
        filters: string[];
    }) => void;
    closeModal: () => void;
    unselectDataset: () => void;
}) => {
    const {
        dataset,
        filters,
        map,
        isMapLoaded,
        openModal,
        closeModal,
        unselectDataset,
    } = input;

    const linkedFilters = pipe(
        filters,
        A.filter((filter) =>
            some(
                filter.filterCriteria,
                (c) => c.dataSourceId === dataset.dataSource.id
            )
        )
    );

    if (isEmpty(linkedFilters)) {
        unselectDataset();
    } else {
        openModal({
            filters: pipe(
                linkedFilters,
                A.map((f) => f.details.name)
            ),
            onClickKeepFilters: () => closeModal(),
            onClickClearFilters: () => {
                linkedFilters.map((filter) => {
                    handleFilterLayers({
                        action: "remove",
                        map,
                        isMapLoaded,
                        suffix: filter.id,
                    });
                });

                closeModal();
            },
        });
    }
};

export const getOrderedDatasetTable = (
    datasets: MapContextDataset[]
): MapContextDataset[] => {
    return orderBy(datasets, (d) => d.tableOrder || 0, ["asc"]);
};

// ███████╗ █████╗ ██╗   ██╗███████╗██████╗     ██╗   ██╗██╗███████╗██╗    ██╗
// ██╔════╝██╔══██╗██║   ██║██╔════╝██╔══██╗    ██║   ██║██║██╔════╝██║    ██║
// ███████╗███████║██║   ██║█████╗  ██║  ██║    ██║   ██║██║█████╗  ██║ █╗ ██║
// ╚════██║██╔══██║╚██╗ ██╔╝██╔══╝  ██║  ██║    ╚██╗ ██╔╝██║██╔══╝  ██║███╗██║
// ███████║██║  ██║ ╚████╔╝ ███████╗██████╔╝     ╚████╔╝ ██║███████╗╚███╔███╔╝
// ╚══════╝╚═╝  ╚═╝  ╚═══╝  ╚══════╝╚═════╝       ╚═══╝  ╚═╝╚══════╝ ╚══╝╚══╝

export const getSavedViewFilters = (): Pick<
    SavedViewDataset,
    "filters" | "configuration"
>[] => {
    const multiFilters = getMultiFilters();

    return pipe(
        multiFilters,
        A.map((m) => ({
            ...omit(m, ["options"]),
            filters: m,
            configuration: {
                showLevelSets: true,
                showPoints: true,
                options: m.options
                    ? {
                          pointMaxGeomsPerCell: m.options.pointMaxGeomsPerCell,
                          pointResolutionOffset:
                              m.options.pointResolutionOffset,
                          polygonMaxGeomsPerCell:
                              m.options.polygonMaxGeomsPerCell,
                          polygonResolutionOffset:
                              m.options.polygonResolutionOffset,
                          levelSets: m.options.levelSets ?? levelSetPercentiles,
                      }
                    : getDefaultDatasetConfigurationOptions(),
            },
        }))
    );
};

export const getContextDatasets = (i: {
    savedViewDatasets: SavedViewDatasetExtended[];
}): MapContextDataset[] => {
    const { savedViewDatasets } = i;

    return pipe(
        savedViewDatasets,
        A.map(
            ({
                dataSource,
                dataset,
                dataSourceConfiguration,
                mapTemplateDataset,
                etlDatasetLocation,
            }) => {
                const filters = dataset.filters;

                return {
                    dataSource,
                    dataSourceConfiguration,
                    mapTemplateDataset: {
                        ...mapTemplateDataset,
                        mapUse: true,
                        styles: dataset.styles || mapTemplateDataset.styles,
                    },
                    isSelected: dataset.selected,
                    isVisible: dataset.selected,
                    isGettingStyled: false,
                    isLegendOpen: false,
                    isTableViewed: dataset.selected,
                    geographyType:
                        etlDatasetLocation?.geographyType || undefined,
                    configuration: {
                        isOpen: false,
                        showLevelSets:
                            dataset.configuration?.showLevelSets ?? true,
                        showPoints: dataset.configuration?.showPoints ?? true,
                        options: dataset.configuration?.options
                            ? {
                                  pointMaxGeomsPerCell:
                                      dataset.configuration?.options
                                          .pointMaxGeomsPerCell,
                                  pointResolutionOffset:
                                      dataset.configuration?.options
                                          .pointResolutionOffset,
                                  polygonMaxGeomsPerCell:
                                      dataset.configuration?.options
                                          .polygonMaxGeomsPerCell,
                                  polygonResolutionOffset:
                                      dataset.configuration?.options
                                          .polygonResolutionOffset,
                                  levelSets:
                                      dataset.configuration?.options
                                          .levelSets ?? levelSetPercentiles,
                              }
                            : getDefaultDatasetConfigurationOptions(),
                    },
                    filters: {
                        logicOperator: DEFAULT_LOGIC_OPERATOR,
                        filters: pipe(
                            filters.filters,
                            toNonReadonlyArray,
                            A.map((item) => ({ ...item, id: uuid() }))
                        ),
                    },
                    selectedRows: toNonReadonlyArray(dataset.selectedRows),
                    pinnedRows: toNonReadonlyArray(dataset.pinnedRows),
                    isSample: false,
                    fkMarketplaceDatasetId: undefined,
                    organization: undefined,
                    color: undefined,
                    image: undefined,
                };
            }
        )
    );
};

export const getSavedViewInputDatasets = (i: {
    datasets: MapContextDataset[];
}): InputSavedViewDataset[] => {
    const { datasets } = i;

    const multiFilters = getMultiFiltersType({ datasets });

    const selectedDatasets = pipe(
        datasets,
        A.filter((d) => isEqual(d.isSelected, true)),
        A.map((d) => d.dataSource.id)
    );

    const input = datasets
        .filter((d) => !d.isSample)
        .map((d) => {
            if (!d.mapTemplateDataset || !d.dataSource.collectionName) {
                return undefined;
            }

            const filters = multiFilters.find(
                (f) => f.databaseId === d.dataSource.id
            );

            return {
                fkDataSourceId: d.dataSource.id,
                fkMapTemplateDatasetId: d.mapTemplateDataset.id,
                selected: selectedDatasets.includes(d.dataSource.id),
                styles: d.mapTemplateDataset
                    ? d.mapTemplateDataset.styles
                    : getDefaultDatasetStyles(),
                configuration: {
                    showLevelSets: d.configuration.showLevelSets,
                    showPoints: d.configuration.showPoints,
                    options: d.configuration.options,
                },
                selectedRows: d.selectedRows || [],
                pinnedRows: d.pinnedRows || [],
                filters: filters ?? {
                    databaseId: d.dataSource.id,
                    collection: d.dataSource.collectionName,
                    logicOperator: LogicOperator.and,
                    databaseType: d.dataSource.type || DatabaseType.point,
                    filters: [],
                },
            };
        });

    return compact(input);
};
